import { TableSort } from "@kyan/ui";
import moment from "moment";

import { type Position } from "#/types/positions";
import {
  getOptionInstrumentProperties,
  isOptionInstrument,
} from "#/utils/instrument.utils";

export const getOptionPositionRows = (
  positions: Position[],
  sort: TableSort<Position>,
  isAllVariant?: boolean,
): { rows: Position[]; dataLength: number } => {
  const optionPositions = positions.filter((position) =>
    isOptionInstrument(position.instrumentType),
  );

  const groupedRows = Object.entries(
    optionPositions.reduce<Record<string, Position[]>>((acc, position) => {
      (acc[position.maturity] ??= []).push(position);
      return acc;
    }, {}),
  )
    .map(([maturity, subPositions]) => {
      const aggregated = subPositions.reduce(
        (acc, pos) => {
          acc.pnl += pos.pnl;
          acc.delta += pos.delta;
          acc.gamma += pos.gamma;
          acc.vega += pos.vega;
          acc.theta += pos.theta;
          acc.rho += pos.rho;
          return acc;
        },
        {
          pnl: 0,
          delta: 0,
          gamma: 0,
          vega: 0,
          theta: 0,
          rho: 0,
        },
      );

      return {
        instrument: maturity,
        instrumentType: "options" as const,
        maturity,
        value: 0,
        size: 0,
        avgPrice: 0,
        markPrice: 0,
        pnl: aggregated.pnl,
        delta: aggregated.delta,
        gamma: aggregated.gamma,
        vega: aggregated.vega,
        theta: aggregated.theta,
        rho: aggregated.rho,
        subPositions:
          sort.key === "instrument"
            ? subPositions.toSorted((a, b) => {
                const { strike: strikeA, optionType: optionTypeA } =
                  getOptionInstrumentProperties(a.instrument);
                const { strike: strikeB } = getOptionInstrumentProperties(
                  b.instrument,
                );

                if (strikeA === strikeB) return optionTypeA === "call" ? -1 : 1;

                return sort.order === "asc"
                  ? strikeA - strikeB
                  : strikeB - strikeA;
              })
            : subPositions,
      };
    })
    .sort((a, b) =>
      moment(a.maturity, "DDMMMYY").diff(moment(b.maturity, "DDMMMYY")),
    );

  const { totalPnl, totalDelta, totalGamma, totalVega, totalTheta, totalRho } =
    optionPositions.reduce(
      (acc, pos) => ({
        totalPnl: acc.totalPnl + pos.pnl,
        totalDelta: acc.totalDelta + pos.delta,
        totalGamma: acc.totalGamma + pos.gamma,
        totalVega: acc.totalVega + pos.vega,
        totalTheta: acc.totalTheta + pos.theta,
        totalRho: acc.totalRho + pos.rho,
      }),
      {
        totalPnl: 0,
        totalDelta: 0,
        totalGamma: 0,
        totalVega: 0,
        totalTheta: 0,
        totalRho: 0,
      },
    );

  const rows =
    isAllVariant || !optionPositions.length
      ? groupedRows
      : [
          {
            instrument: "Total",
            instrumentType: "options" as const,
            maturity: "",
            value: 0,
            size: 0,
            avgPrice: 0,
            markPrice: 0,
            pnl: totalPnl,
            delta: totalDelta,
            gamma: totalGamma,
            vega: totalVega,
            theta: totalTheta,
            rho: totalRho,
            subPositions: [],
          },
          ...groupedRows,
        ];

  return {
    rows,
    dataLength: optionPositions.length,
  };
};
